import type { PlayerColor, YinshState } from "@cloudnest/yinsh-engine";

/**
 * Typed replay-event union for YINSH. Mirrors the persisted relay event types
 * applied in `multiplayer-game-store.ts` so replay reconstructs the exact same
 * state the live client would. YINSH is a public-information game, so there is
 * no viewer-state projection — replay renders canonical state directly.
 */
export type YinshReplayEvent =
  | {
      readonly eventType: "init";
      readonly userId: string;
      readonly payload: { readonly state: YinshState };
    }
  | {
      readonly eventType: "place_ring";
      readonly userId: string;
      readonly payload: {
        readonly player: PlayerColor;
        readonly position: string;
      };
    }
  | {
      readonly eventType: "place_marker";
      readonly userId: string;
      readonly payload: {
        readonly player: PlayerColor;
        readonly ringPosition: string;
      };
    }
  | {
      readonly eventType: "move_ring";
      readonly userId: string;
      readonly payload: {
        readonly player: PlayerColor;
        readonly from: string;
        readonly to: string;
      };
    }
  | {
      readonly eventType: "take_turn";
      readonly userId: string;
      readonly payload: {
        readonly player: PlayerColor;
        readonly ringPosition: string;
        readonly destination: string;
      };
    }
  | {
      readonly eventType: "select_row";
      readonly userId: string;
      readonly payload: {
        readonly player: PlayerColor;
        readonly positions: readonly [string, string, string, string, string];
      };
    }
  | {
      readonly eventType: "remove_ring";
      readonly userId: string;
      readonly payload: {
        readonly player: PlayerColor;
        readonly position: string;
      };
    }
  | {
      readonly eventType: "forfeit";
      readonly userId: string;
      readonly payload: { readonly player: PlayerColor };
    };

export type YinshReplayEventType = YinshReplayEvent["eventType"];

export interface YinshEventInput {
  readonly eventType: string;
  readonly userId?: string;
  readonly payload?: Record<string, unknown>;
}

export const YINSH_REPLAY_EVENT_TYPES = [
  "init",
  "place_ring",
  "place_marker",
  "move_ring",
  "take_turn",
  "select_row",
  "remove_ring",
  "forfeit",
] as const satisfies readonly YinshReplayEventType[];

function requireUserId(event: YinshEventInput): string {
  if (typeof event.userId === "string" && event.userId.length > 0) {
    return event.userId;
  }
  throw new Error("YINSH replay event is missing userId");
}

export function normalizeYinshReplayEventType(
  eventType: string,
): YinshReplayEventType {
  switch (eventType) {
    case "init":
    case "place_ring":
    case "place_marker":
    case "move_ring":
    case "take_turn":
    case "select_row":
    case "remove_ring":
    case "forfeit":
      return eventType;
    default:
      throw new Error(`Unsupported YINSH event type: ${eventType}`);
  }
}

export function toYinshReplayEvent(event: YinshEventInput): YinshReplayEvent {
  const eventType = normalizeYinshReplayEventType(event.eventType);
  const userId = requireUserId(event);
  const payload = event.payload ?? {};

  switch (eventType) {
    case "init":
      return { eventType, userId, payload: payload as { state: YinshState } };
    case "place_ring":
      return {
        eventType,
        userId,
        payload: payload as { player: PlayerColor; position: string },
      };
    case "place_marker":
      return {
        eventType,
        userId,
        payload: payload as { player: PlayerColor; ringPosition: string },
      };
    case "move_ring":
      return {
        eventType,
        userId,
        payload: payload as { player: PlayerColor; from: string; to: string },
      };
    case "take_turn":
      return {
        eventType,
        userId,
        payload: payload as {
          player: PlayerColor;
          ringPosition: string;
          destination: string;
        },
      };
    case "select_row":
      return {
        eventType,
        userId,
        payload: payload as {
          player: PlayerColor;
          positions: readonly [string, string, string, string, string];
        },
      };
    case "remove_ring":
      return {
        eventType,
        userId,
        payload: payload as { player: PlayerColor; position: string },
      };
    case "forfeit":
      return {
        eventType,
        userId,
        payload: payload as { player: PlayerColor },
      };
  }
}
